// src/lib/moderation.js
/* =========================================================================
   Messaging Moderation for You’re Welcome (ROBUST + LOW FALSE-POSITIVES)
   Goals:
   - Catch off-platform & contact/payment sharing before booking
   - Catch obfuscated numbers:
       "three fourteen nine thirty-three"
       "3️⃣1️⃣4️⃣9️⃣3️⃣3️⃣4️⃣9️⃣6️⃣6️⃣"
       "(three)(one)(four)"
   - Catch split numbers across messages via windowText
   - DO NOT block normal chat like "hello"

   Usage:
     const windowText = buildWindowText(recentMessages);
     const verdict = moderateMessage({ text, windowText, bookingStatus });

   verdict.action: "block" | "warn" | "allow"
   ========================================================================= */

// --------------------------- Normalization ------------------------------

export function normalizeText(raw = "") {
  let s = String(raw).toLowerCase();

  // Unicode normalize + strip diacritics
  try {
    s = s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  } catch (_) {}

  // Remove zero-width / invisible chars
  s = s.replace(/[\u200B-\u200D\uFEFF]/g, "");

  // Normalize fancy whitespace to spaces
  s = s.replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, " ");

  // Turn brackets/parentheses into spaces so "(three)(one)(four)" tokenizes
  s = s.replace(/[\(\)\[\]\{\}<>]/g, " ");

  // Map common obfuscations (word->symbol)
  s = s
    .replace(/\b(at|arroba)\b/g, "@")
    .replace(/\b(dot|d0t|point|period)\b/g, ".");

  // Convert emoji digits/circled digits to ascii digits
  s = emojiDigitsToAscii(s);

  // Safer leetspeak coercion:
  // only coerce inside digit-ish runs, not normal words ("hello" => "he110" bug)
  s = s.replace(
    /(^|[^a-z0-9])([oilssb]+)(?=[^a-z0-9]|$)/gi,
    (m, pre, token) => pre + token
      .replace(/o/gi, "0")
      .replace(/[il]/gi, "1")
      .replace(/s/gi, "5")
      .replace(/b/gi, "8")
  );

  return s;
}

function stripNonDigits(s) {
  return String(s || "").replace(/[^\d]/g, "");
}

// Build a window string from recent messages
export function buildWindowText(recentMessages = []) {
  return recentMessages
    .map((m) => (typeof m === "string" ? m : (m?.text || "")))
    .join(" ");
}

// ----------------------- Emoji digit normalization ----------------------

// Keycap digits: 0️⃣..9️⃣
const KEYCAP_MAP = {
  "0️⃣": "0", "1️⃣": "1", "2️⃣": "2", "3️⃣": "3", "4️⃣": "4",
  "5️⃣": "5", "6️⃣": "6", "7️⃣": "7", "8️⃣": "8", "9️⃣": "9",
};

// Common digit symbols/circled numbers
const OTHER_DIGIT_MAP = {
  "⓪": "0","①":"1","②":"2","③":"3","④":"4","⑤":"5","⑥":"6","⑦":"7","⑧":"8","⑨":"9",
  "❶":"1","❷":"2","❸":"3","❹":"4","❺":"5","❻":"6","❼":"7","❽":"8","❾":"9",
};

function emojiDigitsToAscii(s) {
  let out = String(s || "");

  for (const [k, v] of Object.entries(KEYCAP_MAP)) {
    out = out.split(k).join(v);
  }
  for (const [k, v] of Object.entries(OTHER_DIGIT_MAP)) {
    out = out.split(k).join(v);
  }

  return out;
}

// -------------------- Advanced word→digits conversion -------------------

const ONE_TO_NINE = {
  zero: "0", oh: "0",
  one: "1", two: "2", three: "3", four: "4", five: "5",
  six: "6", seven: "7", eight: "8", nine: "9",
};

const TEENS = {
  ten: "10", eleven: "11", twelve: "12", thirteen: "13", fourteen: "14",
  fifteen: "15", sixteen: "16", seventeen: "17", eighteen: "18", nineteen: "19",
};

const TENS = {
  twenty: "20", thirty: "30", forty: "40", fifty: "50",
  sixty: "60", seventy: "70", eighty: "80", ninety: "90",
};

// Convert sequences like:
// "three fourteen nine thirty-three" => "314933"
// "nine thirty three" => "933"
function wordsToDigitsAdvanced(s) {
  const cleaned = String(s || "").replace(/-/g, " ");
  const tokens = cleaned.split(/\s+/).filter(Boolean);

  let out = "";
  let i = 0;

  while (i < tokens.length) {
    const t = tokens[i];

    if (ONE_TO_NINE[t] != null) {
      out += ONE_TO_NINE[t];
      i += 1;
      continue;
    }

    if (TEENS[t] != null) {
      out += TEENS[t];
      i += 1;
      continue;
    }

    if (TENS[t] != null) {
      const base = Number(TENS[t]); // 30
      const next = tokens[i + 1];
      if (next && ONE_TO_NINE[next] != null) {
        out += String(base + Number(ONE_TO_NINE[next])); // 33
        i += 2;
      } else {
        out += String(base);
        i += 1;
      }
      continue;
    }

    i += 1;
  }

  return out;
}

// ----------------------------- Regexes ----------------------------------

// Standard email (after normalize + wordsToDigitsAdvanced)
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;

// Broad URL detection
const URL_RE =
  /\b(?:https?:\/\/|www\.)[^\s]+|\b[a-z0-9-]+\.(?:com|net|org|io|co|ai|app|me|tv|info|biz|ly|gg|link)\b/i;

// Phone pattern (US-ish w/ optional +, separators)
const PHONE_RE =
  /(?:\+?\d[\s\-\.]*)?(?:\(?\d{3}\)?[\s\-\.]*)\d{3}[\s\-\.]*\d{4}\b/;

// Handle-ish patterns: "@username"
const HANDLE_RE = /(?:^|[\s])@([a-z0-9._-]{2,30})\b/i;

// Avoid detecting numeric non-contact patterns:
// prices, dates, times, unit counts
const PRICE_RE = /\$\s?\d{1,5}(?:[.,]\d{2})?|\b\d{1,5}(?:[.,]\d{2})?\s?(?:usd|dollars|per hour|per night|per day)\b/i;
const DATE_RE  = /\b(?:\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})\b/;
const TIME_RE  = /\b\d{1,2}:\d{2}\s?(?:am|pm)?\b/;
const UNIT_RE  = /\b(?:\d{1,4}\s?(?:guests|people|hours|days|nights|beds|rooms))\b/i;

// ----------------------------- Keywords ---------------------------------

const HARD_KEYWORDS = [
  "whatsapp", "telegram", "signal", "instagram", "insta", "snapchat",
  "facebook", "messenger", "discord", "skype", "imessage",
  "text me", "call me", "my number", "phone number", "cell number",
  "email me", "reach me at", "contact me directly", "dm me",
  "message me on", "hit me up on", "add me on", "follow me on",
  "send me your number", "share your number", "here is my number",
];

const PAYMENT_KEYWORDS = [
  "venmo", "cashapp", "cash app", "zelle", "paypal", "apple pay",
  "google pay", "wire me", "bank transfer", "square",
];

const SOFT_KEYWORDS = [
  "off app", "off-app", "off platform", "off-platform", "outside the app",
  "book directly", "direct booking", "avoid fees", "avoid the fee",
  "avoid service fee", "save fees", "cheaper if", "discount if",
  "off platform discount", "cash discount",
  "pay cash", "pay outside", "do it directly", "not through the app",
  "without the app", "without fees",
];

const ALLOW_CONTEXT_KEYWORDS = [
  "gate code", "door code", "lock code", "wifi", "wi-fi", "password",
  "check in", "check-in", "checkout", "check-out",
];

// ----------------------------- Helpers ----------------------------------

function includesAny(haystack, list) {
  const h = String(haystack || "");
  return list.find((k) => h.includes(k));
}

function isMostlyNoiseNumberContext(text) {
  const t = String(text || "");
  if (PRICE_RE.test(t)) return true;
  if (DATE_RE.test(t)) return true;
  if (TIME_RE.test(t)) return true;
  if (UNIT_RE.test(t)) return true;
  return false;
}

function digitsLookLikePhone(digits) {
  // strict: only treat as phone when it is clearly phone length
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith("1")) return true;
  return false;
}

function hasSplitPhoneAcrossWindow(t0Digits, twDigits) {
  // combine current+window digits
  const combined = t0Digits + twDigits;
  if (digitsLookLikePhone(combined)) return true;

  // also try chunk-join for cases like "314" "933" "4966"
  const chunks = (t0Digits + " " + twDigits).match(/\d{2,4}/g) || [];
  const joined = chunks.join("");
  if (digitsLookLikePhone(joined)) return true;

  return false;
}

// --------------------------- Main Moderation ----------------------------

export function moderateMessage({
  text,
  windowText = "",
  bookingStatus = "none", // "none"|"pending"|"confirmed"|"active"|"completed"|"canceled"
}) {
  const hard = [];
  const soft = [];

  const t0 = normalizeText(text);
  const tw = normalizeText(windowText);

  // Convert word-number sequences to digits
  const t0wordsDigits = wordsToDigitsAdvanced(t0);
  const twwordsDigits = wordsToDigitsAdvanced(tw);

  // Append digit-converted stream back into text so regex can find it
  const t0d = t0 + " " + t0wordsDigits;
  const twd = tw + " " + twwordsDigits;

  const preBooking = bookingStatus === "none" || bookingStatus === "pending";

  // allow-context helps prevent soft false-positives
  const allowContext = includesAny(twd, ALLOW_CONTEXT_KEYWORDS);

  // 1) Emails (either current or window)
  if (EMAIL_RE.test(t0d) || EMAIL_RE.test(twd)) {
    hard.push("email");
  }

  // 2) URLs
  if (URL_RE.test(t0d) || URL_RE.test(twd)) {
    hard.push("url_or_external_link");
  }

  // 3) Social handles
  // Handle alone is not enough to hard-block unless paired with social/hard keywords.
  const handleHit = HANDLE_RE.test(t0d) || HANDLE_RE.test(twd);
  if (handleHit) {
    const socialContext =
      includesAny(twd, ["instagram", "insta", "snapchat", "facebook", "discord", "telegram", "whatsapp"]) ||
      includesAny(t0d, ["instagram", "insta", "snapchat", "facebook", "discord", "telegram", "whatsapp"]);
    if (socialContext) hard.push("social_handle");
    else soft.push("handle_without_platform");
  }

  // 4) Phones
  const hasPhonePattern = PHONE_RE.test(t0d) || PHONE_RE.test(twd);
  if (hasPhonePattern) {
    // Guard against noise contexts like "$314" etc.
    if (!isMostlyNoiseNumberContext(t0d) && !isMostlyNoiseNumberContext(twd)) {
      hard.push("phone_pattern");
    }
  } else {
    const d0 = stripNonDigits(t0d);
    const dw = stripNonDigits(twd);

    // Only hard if current text alone is clear phone length
    if (!isMostlyNoiseNumberContext(t0d) && digitsLookLikePhone(d0)) {
      hard.push("phone_digits");
    } else {
      // Split/obfuscated across window
      if (
        !isMostlyNoiseNumberContext(t0d) &&
        !isMostlyNoiseNumberContext(twd) &&
        hasSplitPhoneAcrossWindow(d0, dw)
      ) {
        hard.push("phone_digits_window");
      }
    }
  }

  // 5) Explicit hard keywords
  const hardHit = includesAny(twd, HARD_KEYWORDS) || includesAny(t0d, HARD_KEYWORDS);
  if (hardHit) hard.push(`keyword:${hardHit}`);

  // 6) Payment handles
  const payHit = includesAny(twd, PAYMENT_KEYWORDS) || includesAny(t0d, PAYMENT_KEYWORDS);
  if (payHit) hard.push(`payment:${payHit}`);

  // 7) Soft intent cues (fee avoidance / off-platform)
  const softHit = includesAny(twd, SOFT_KEYWORDS) || includesAny(t0d, SOFT_KEYWORDS);
  if (softHit && !allowContext) {
    soft.push(`intent:${softHit}`);
  }

  // ---------------------- Enforcement by status ------------------------

  let action = "allow";

  if (preBooking) {
    // Pre-booking:
    // - Hard contact/payment/links => block
    // - Soft off-platform intent => warn
    if (hard.length) action = "block";
    else if (soft.length) action = "warn";
  } else {
    // Post-booking / active stay:
    // - Payment rails still blocked
    // - Everything else -> warn (not block)
    const postBookingHard = hard.filter((h) => h.startsWith("payment:"));
    if (postBookingHard.length) action = "block";
    else if (hard.length || soft.length) action = "warn";
  }

  return {
    ok: action !== "block",
    action,
    hard,
    soft,
    bookingStatus,
    preBooking,
  };
}
